import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

// =====================================================
// STATIC FAQ DATA
// =====================================================

const faqs = [
  {
    question: "What legal services do you provide for businesses?",
    answer:
      "We provide complete business legal support including company registration, business licensing, taxation assistance, statutory compliance, intellectual property services, certifications, legal documentation, and ongoing compliance support.",
  },
  {
    question: "How long does the registration or legal process take?",
    answer:
      "The timeline depends on the type of service, required documentation, government processing time, and verification requirements. We provide an estimated timeline at the beginning and keep you updated throughout the process.",
  },
  {
    question: "What documents are required to get started?",
    answer:
      "Required documents vary depending on the service and business structure. Generally, identity proof, address proof, business details, ownership documents, and supporting records may be required. We provide a clear checklist based on your specific requirements.",
  },
  {
    question: "Do you provide support after registration or approval?",
    answer:
      "Yes. Our support continues beyond registration. We can assist with renewals, statutory filings, taxation requirements, certifications, documentation, and other ongoing compliance needs to help your business remain compliant.",
  },
];

// =====================================================
// FAQ SECTION
// =====================================================

const FAQSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);

  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        text-[var(--text-primary)]
        py-6
        transition-colors
        duration-500
        sm:py-8
        md:py-10
        lg:py-14
      "
    >
      {/* =====================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          pointer-events-none
          absolute
          -left-40
          top-20
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-80
          sm:w-80
        "
      />

      <div
        className="
          pointer-events-none
          absolute
          -right-40
          bottom-10
          h-64
          w-64
          rounded-full
          bg-orange-500/[0.04]
          blur-[110px]
          sm:h-80
          sm:w-80
        "
      />

      {/* =====================================================
          MAIN CONTAINER
      ====================================================== */}

      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        {/* =====================================================
            HEADER
        ====================================================== */}

        <motion.div
          initial={{
            opacity: 0,
            y: 25,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
            amount: 0.2,
          }}
          transition={{
            duration: 0.6,
            ease: "easeOut",
          }}
          className="
            mx-auto
            mb-8
            max-w-3xl
            text-center
            sm:mb-10
            lg:mb-12
          "
        >
          {/* Label */}

          <motion.span
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.5,
            }}
            className="
              inline-flex
              rounded-full
              border
              border-orange-500/20
              bg-orange-500/[0.08]
              px-3
              py-1.5
              text-[10px]
              font-semibold
              uppercase
              tracking-[2px]
              text-orange-400
              sm:px-4
              sm:py-2
              sm:text-xs
            "
          >
            FAQs
          </motion.span>

          {/* Heading */}

          <h2
            className="
              mt-4
              text-2xl
              font-bold
              leading-tight
              tracking-tight
              text-[var(--text-primary)]
              transition-colors
              duration-500
              sm:mt-5
              sm:text-3xl
              md:text-4xl
              lg:text-5xl
            "
          >
            Frequently Asked{" "}
            <span className="text-[var(--brand-primary)]">
              Questions
            </span>
          </h2>

          {/* Description */}

          <p
            className="
              mx-auto
              mt-3
              max-w-5xl
              text-xs
              leading-5
              text-[var(--text-muted)]
              transition-colors
              duration-500
              sm:mt-4
              sm:text-sm
              sm:leading-6
              md:text-base
              md:leading-7
            "
          >
            Get quick answers to common questions about our legal
            services, documentation, registration process, and
            ongoing business compliance support.
          </p>
        </motion.div>

        {/* =====================================================
            FAQ GRID
        ====================================================== */}

        <div
          className="
            mx-auto
            grid
            w-full
            max-w-6xl
            grid-cols-1
            gap-3
            sm:gap-4
          "
        >
          {faqs.map((faq, index) => {
            const isOpen = activeIndex === index;

            return (
              <motion.div
                key={faq.question}
                initial={{
                  opacity: 0,
                  y: 20,
                }}
                whileInView={{
                  opacity: 1,
                  y: 0,
                }}
                viewport={{
                  once: true,
                  amount: 0.1,
                }}
                transition={{
                  duration: 0.45,
                  delay: index * 0.08,
                  ease: "easeOut",
                }}
                className="
                  group
                  overflow-hidden
                  rounded-xl
                  border
                  border-[var(--border-primary)]
                  bg-[var(--card-bg-secondary)]
                  text-[var(--text-primary)]
                  transition-all
                  duration-300
                  hover:border-orange-500/30
                  sm:rounded-2xl
                "
              >
                {/* =================================================
                    QUESTION
                ================================================== */}

                <button
                  type="button"
                  onClick={() =>
                    setActiveIndex(
                      isOpen ? -1 : index
                    )
                  }
                  aria-expanded={isOpen}
                  className="
                    flex
                    w-full
                    items-center
                    justify-between
                    gap-4
                    px-4
                    py-4
                    text-left
                    transition-all
                    duration-300
                    hover:bg-[var(--bg-tertiary)]
                    sm:px-5
                    sm:py-5
                    md:px-6
                  "
                >
                  <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                    {/* Number */}

                    <span
                      className="
                        shrink-0
                        text-[10px]
                        font-semibold
                        tracking-[1.5px]
                        text-orange-500/70
                        sm:text-xs
                      "
                    >
                      0{index + 1}
                    </span>

                    {/* Question */}

                    <h3
                      className="
                        text-sm
                        font-semibold
                        leading-5
                        text-[var(--text-primary)]
                        transition-colors
                        duration-300
                        group-hover:text-orange-400
                        sm:text-base
                        sm:leading-6
                        md:text-lg
                      "
                    >
                      {faq.question}
                    </h3>
                  </div>

                  {/* Plus / Minus */}

                  <div
                    className={`
                      flex
                      h-8
                      w-8
                      shrink-0
                      items-center
                      justify-center
                      rounded-full
                      border
                      transition-all
                      duration-300
                      sm:h-9
                      sm:w-9
                      ${
                        isOpen
                          ? "border-[var(--brand-primary)] bg-[var(--brand-primary)] text-white"
                          : "border-orange-500/20 bg-orange-500/[0.08] text-[var(--brand-primary)]"
                      }
                    `}
                  >
                    {isOpen ? (
                      <Minus size={16} />
                    ) : (
                      <Plus size={16} />
                    )}
                  </div>
                </button>

                {/* =================================================
                    ANSWER
                ================================================== */}

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      initial={{
                        height: 0,
                        opacity: 0,
                      }}
                      animate={{
                        height: "auto",
                        opacity: 1,
                      }}
                      exit={{
                        height: 0,
                        opacity: 0,
                      }}
                      transition={{
                        duration: 0.3,
                        ease: "easeInOut",
                      }}
                    >
                      <div
                        className="
                          border-t
                          border-[var(--border-secondary)]
                          px-4
                          pb-4
                          pt-3
                          transition-colors
                          duration-500
                          sm:px-5
                          sm:pb-5
                          sm:pt-4
                          md:px-6
                        "
                      >
                        <p
                          className="
                            pl-7
                            text-xs
                            leading-6
                            text-[var(--text-muted)]
                            transition-colors
                            duration-500
                            sm:pl-9
                            sm:text-sm
                            sm:leading-6
                          "
                        >
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;